import { type ReactNode, useEffect, useState } from 'react'
import { QrCode } from './QrCode'

// Sending an offer by WeChat. WeChat has no web "share" link the way WhatsApp has wa.me, so the two ways
// that work today are the ones people use: scan the code with the phone and forward the page into the
// client's chat, or paste a ready message (Chinese first, English under it). Shared by the product's rate
// desk and the partner demo; `note` is an optional footer line each app words for itself.

export const WECHAT_GREEN = '#07c160'

export function WeChatGlyph({ size = 16 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" fill="currentColor">
      <path d="M9.2 4C5.2 4 2 6.7 2 10c0 1.9 1 3.5 2.6 4.6L4 17l2.6-1.4c.8.2 1.7.4 2.6.4h.5a5.6 5.6 0 0 1-.2-1.5c0-3.4 3.2-6.1 7.2-6.1h.5C16.5 5.9 13.2 4 9.2 4Zm-2.4 3.4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4.8 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z" />
      <path d="M22 14.5c0-2.8-2.7-5-6-5s-6 2.2-6 5 2.7 5 6 5c.7 0 1.4-.1 2-.3l2 1.1-.5-1.9c1.5-.9 2.5-2.3 2.5-3.9Zm-8-1.6a.8.8 0 1 1 0 1.6.8.8 0 0 1 0-1.6Zm4 0a.8.8 0 1 1 0 1.6.8.8 0 0 1 0-1.6Z" />
    </svg>
  )
}

export function wechatMessage(o: {
  client: string
  ref: string
  route: string
  total: string
  url: string
}): string {
  return [
    `${o.client} 您好，这是您的运价报价 ${o.ref}（${o.route}），合计 ${o.total}。请点击链接查看并确认：${o.url}`,
    '',
    `Hello ${o.client}, here is your freight offer ${o.ref} (${o.route}), ${o.total} in total. Review and confirm: ${o.url}`,
  ].join('\n')
}

export function WeChatSheet({
  url,
  message,
  client,
  note,
  onClose,
}: {
  url: string
  message: string
  client: string
  note?: ReactNode
  onClose: () => void
}) {
  const [copied, setCopied] = useState<'' | 'message' | 'link'>('')
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])
  const copy = (what: 'message' | 'link') =>
    navigator.clipboard
      ?.writeText(what === 'message' ? message : url)
      .then(() => setCopied(what))
      .catch(() => setCopied(''))

  return (
    <div className="wx-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <dialog open className="wx" aria-label="Send by WeChat">
        <header>
          <span className="wx-badge">
            <WeChatGlyph size={18} />
          </span>
          <div>
            <h3>
              Send by WeChat <span lang="zh">微信发送</span>
            </h3>
            <p>
              {client} opens the offer inside WeChat, signs in with WeChat in one tap and confirms.
              You see it here the moment they do.
            </p>
          </div>
          <button type="button" className="wx-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="wx-body">
          <figure>
            <QrCode text={url} label="QR code of the offer link" />
            <figcaption>
              <b>Scan with WeChat</b>
              <span>
                On your phone: WeChat › Scan. The offer opens; tap ··· › Send to Chat and pick{' '}
                {client}.
              </span>
            </figcaption>
          </figure>
          <div className="wx-msg">
            <span className="wx-label">Or paste into the chat</span>
            <pre>{message}</pre>
            <div className="wx-actions">
              <button type="button" className="wx-primary" onClick={() => copy('message')}>
                {copied === 'message' ? 'Copied ✓' : 'Copy message'}
              </button>
              <button type="button" onClick={() => copy('link')}>
                {copied === 'link' ? 'Link copied ✓' : 'Copy link only'}
              </button>
            </div>
          </div>
        </div>
        {note && <footer>{note}</footer>}
      </dialog>
      <style>{wxStyles}</style>
    </div>
  )
}

const wxStyles = `
.wx-scrim{position:fixed;inset:0;z-index:1400;display:grid;place-items:center;padding:16px;background:rgb(10 14 20/.42);backdrop-filter:blur(3px)}
.wx{position:relative;margin:0;width:min(640px,100%);max-height:calc(100vh - 32px);overflow:auto;border:0;border-radius:20px;background:#fff;color:#0b0c0e;padding:0;box-shadow:0 40px 90px -30px rgb(10 20 40/.55);font-family:Switzer,ui-sans-serif,system-ui,sans-serif;text-align:left}
.wx *{box-sizing:border-box}
.wx header{display:grid;grid-template-columns:auto 1fr auto;gap:14px;align-items:start;padding:20px 22px 16px;border-bottom:1px solid rgb(14 20 27/.08)}
.wx-badge{display:grid;place-items:center;width:38px;height:38px;border-radius:11px;background:${WECHAT_GREEN};color:#fff}
.wx h3{margin:0;font-size:18px;font-weight:650;letter-spacing:-.01em}.wx h3 span{color:#788392;font-weight:500;margin-left:6px}
.wx header p{margin:4px 0 0;font-size:13px;line-height:1.5;color:#525e6b}
.wx-x{border:0;background:rgb(14 20 27/.06);width:30px;height:30px;border-radius:50%;font-size:18px;line-height:1;cursor:pointer;color:#525e6b}
.wx-body{display:grid;grid-template-columns:auto 1fr;gap:22px;padding:20px 22px}
.wx figure{margin:0;display:grid;gap:10px;justify-items:center;width:196px}
.wx figure svg{border-radius:12px;box-shadow:0 0 0 1px rgb(14 20 27/.08)}
.wx figcaption{display:grid;gap:3px;text-align:center}.wx figcaption b{font-size:13px}.wx figcaption span{font-size:12px;color:#525e6b;line-height:1.45}
.wx-label{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#788392;font-weight:700}
.wx-msg{display:grid;align-content:start;gap:10px;min-width:0}
.wx pre{margin:0;white-space:pre-wrap;word-break:break-word;font:inherit;font-size:13px;line-height:1.55;background:#f4f6f8;border-radius:12px;padding:12px 14px;color:#0b0c0e}
.wx-actions{display:flex;flex-wrap:wrap;gap:8px}
.wx-actions button{border:1px solid rgb(14 20 27/.14);background:#fff;color:#0b0c0e;border-radius:10px;padding:9px 14px;font:inherit;font-size:13px;font-weight:600;cursor:pointer}
.wx-actions .wx-primary{background:${WECHAT_GREEN};border-color:${WECHAT_GREEN};color:#fff}
.wx footer{padding:12px 22px 18px;font-size:12px;color:#788392;line-height:1.5;border-top:1px solid rgb(14 20 27/.06)}
@media(max-width:560px){.wx-body{grid-template-columns:1fr}.wx figure{width:auto}}
`
